import React, { useRef } from 'react';
import { Upload, X, CheckCircle, AlertCircle } from 'lucide-react';
import ProcessingButton from './ProcessingButton';

const FileUploadInput = ({ 
  uploadQueue, 
  onFileSelect, 
  onRemoveFromQueue, 
  onUploadFiles, 
  isUploading, 
  fileTypes 
}) => {
  const fileInputRef = useRef(null);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'uploading': return <Upload className="w-5 h-5 text-blue-500 animate-pulse" />;
      default: return <Upload className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Supported File Types */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {Object.entries(fileTypes).filter(([key]) => !['rss', 'news'].includes(key)).map(([key, config]) => {
          const IconComponent = config.icon;
          return (
            <div key={key} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <IconComponent className={`w-4 h-4 text-${config.color}-500`} />
              <div className="text-sm">
                <div className="font-medium">{key.toUpperCase()}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Max: {config.maxSize}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* File Input */}
      <div 
        className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
          Click to upload files
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          PDF, Excel, CSV, Text files supported
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={onFileSelect}
          accept=".pdf,.xlsx,.xls,.csv,.txt,.md"
        />
      </div>

      {/* Upload Queue */}
      {uploadQueue.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700 dark:text-gray-300">Upload Queue</h4>
          {uploadQueue.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="flex items-center gap-3">
                {getStatusIcon(item.status)}
                <div>
                  <div className="font-medium text-sm">{item.file.name}</div>
                  <div className="text-xs text-gray-500">
                    {(item.file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {item.status === 'uploading' && (
                  <div className="w-16 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${item.progress}%` }}
                    ></div>
                  </div>
                )}
                {item.status === 'pending' && (
                  <button
                    onClick={() => onRemoveFromQueue(item.id)}
                    className="p-1 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          
          {uploadQueue.some(item => item.status === 'pending') && (
            <ProcessingButton
              onClick={onUploadFiles}
              disabled={isUploading}
              isLoading={isUploading}
              loadingText="Uploading..."
              variant="primary"
            >
              Upload All Files
            </ProcessingButton>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUploadInput;
